import React from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { ProposalPage } from './pages/public/ProposalPage';
import { EcommerceDemo } from './pages/public/EcommerceDemo';
import { LandingPageDemo } from './pages/public/LandingPageDemo';
import { BiolinkDemo } from './pages/public/BiolinkDemo';
import { ResellerDemo } from './pages/public/ResellerDemo';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { Prospect } from './shared/types';
import { AppDemo } from './pages/public/AppDemo';
import { PortalDemo } from './pages/public/PortalDemo';
import { PlataformaDemo } from './pages/public/PlataformaDemo';
import { PackageHotpageDemo } from './pages/public/PackageHotpageDemo';
import { LawDemo } from './pages/public/LawDemo';
import { RealEstateDemo } from './pages/public/RealEstateDemo';
import { RealEstateHotpage } from './pages/public/RealEstateHotpage';
import { VerticalCanvasProposal } from './pages/public/VerticalCanvasProposal';
import { SolutionsHub } from './pages/public/SolutionsHub';
import { SegmentOverview } from './pages/public/SegmentOverview';
import { PublicHomePage } from './pages/public/PublicHomePage';
import { DigitalMenuDemo } from './pages/public/DigitalMenuDemo';
import { HotpageDemo } from './pages/public/HotpageDemo';
import { SchoolDemo } from './pages/public/SchoolDemo';
import { AutoDemo } from './pages/public/AutoDemo';
import { HealthDemo } from './pages/public/HealthDemo';
import { ServiceDemo } from './pages/public/ServiceDemo';
import { ArchDemo } from './pages/public/ArchDemo';
import { AgencyDemo } from './pages/public/AgencyDemo';
import { GastronomyDemo } from './pages/public/GastronomyDemo';
import { mockProspects } from './shared/data/prospects';

const resolveProspect = (slug?: string): Prospect => {
  if (slug && mockProspects[slug]) {
    return mockProspects[slug];
  }
  return {
    id: `prsp_${slug || 'empresa'}`,
    name: `${slug || 'empresa'} Empreendimentos`,
    trade_name: (slug || 'Empresa').replace(/-/g, ' ').toUpperCase(),
    slug: slug || 'empresa',
    segment: 'Serviços Especializados',
    city: 'São Miguel do Oeste',
    state: 'SC',
    whatsapp: '554988416296',
    status: 'ACTIVE',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

export const App: React.FC = () => {
  return (
    <Router>
      <Routes>
        {/* Admin Backoffice Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
        </Route>

        {/* Public Landing Page for YouMachine */}
        <Route path="/" element={<PublicHomePage />} />

        {/* Public Solutions Library Routes */}
        <Route path="/solucoes" element={<SolutionsHub />} />
        <Route path="/solucoes/:segmentSlug" element={<SegmentOverview />} />
        <Route path="/solucoes/restaurantes/cardapio" element={<DigitalMenuRouteWrapper />} />
        <Route path="/solucoes/escolas/site" element={<SchoolDemo />} />
        <Route path="/solucoes/automoveis/site" element={<AutoDemo prospect={resolveProspect('concessionaria')} />} />
        <Route path="/solucoes/clinicas/site" element={<HealthDemo prospect={resolveProspect('clinica')} />} />
        <Route path="/solucoes/servicos/site" element={<ServiceDemo prospect={resolveProspect('engenharia')} />} />
        <Route path="/solucoes/arquitetura/site" element={<ArchDemo prospect={resolveProspect('arquitetura')} />} />
        <Route path="/solucoes/agencia/site" element={<AgencyDemo prospect={resolveProspect('agencia')} />} />
        <Route path="/solucoes/hotpage/:launchType" element={<HotpageRouteWrapper />} />
        <Route path="/proposta-canvas" element={<VerticalCanvasProposal />} />
        <Route path="/biolink" element={<BiolinkRouteWrapper />} />
        <Route path="/biolink-demo" element={<BiolinkRouteWrapper />} />

        {/* Dynamic Public Routes by Slug */}
        <Route path="/:slug" element={<ProposalPage />} />
        <Route path="/:slug/proposta-canvas" element={<VerticalCanvasProposal />} />
        <Route path="/:slug/ecommerce" element={<EcommerceRouteWrapper />} />
        <Route path="/:slug/landingpage" element={<LandingPageRouteWrapper />} />
        <Route path="/:slug/biolink" element={<BiolinkRouteWrapper />} />
        <Route path="/:slug/revendedoras-demo" element={<ResellerRouteWrapper />} />
        
        {/* New Wireframe Routes */}
        <Route path="/:slug/app" element={<AppRouteWrapper />} />
        <Route path="/:slug/site" element={<PortalRouteWrapper />} />
        <Route path="/:slug/advocacia" element={<LawRouteWrapper />} />
        <Route path="/:slug/imobiliaria" element={<RealEstateRouteWrapper />} />
        <Route path="/:slug/hotpage" element={<RealEstateHotpageRouteWrapper />} />
        <Route path="/:slug/automoveis" element={<AutoRouteWrapper />} />
        <Route path="/:slug/clinica" element={<HealthRouteWrapper />} />
        <Route path="/:slug/servicos" element={<ServiceRouteWrapper />} />
        <Route path="/:slug/arquitetura" element={<ArchRouteWrapper />} />
        <Route path="/:slug/agencia" element={<AgencyRouteWrapper />} />
        <Route path="/:slug/cardapio" element={<DigitalMenuRouteWrapper />} />
        <Route path="/:slug/gastronomia" element={<GastronomyRouteWrapper />} />
        <Route path="/:slug/sistema" element={<PlataformaRouteWrapper />} />
        <Route path="/:slug/plataforma-demo" element={<PlataformaRouteWrapper />} />
        <Route path="/:slug/pacotes/:packageSlug" element={<PackageHotpageRouteWrapper />} />
        <Route path="/:slug/campanhas/:campaignSlug" element={<CampaignRouteWrapper />} />
        <Route path="/:slug/gift-card" element={<GiftCardRouteWrapper />} />
      </Routes>
    </Router>
  );
};

// Route Wrappers to pass dynamically resolved prospect
const EcommerceRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <EcommerceDemo prospect={resolveProspect(slug)} />;
};

const AppRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <AppDemo prospect={resolveProspect(slug)} />;
};

const PortalRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <PortalDemo prospect={resolveProspect(slug)} />;
};

const PlataformaRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <PlataformaDemo prospect={resolveProspect(slug)} />;
};

const CampaignRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <LandingPageDemo prospect={resolveProspect(slug)} />;
};

const GiftCardRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <LandingPageDemo prospect={resolveProspect(slug)} />;
};

const PackageHotpageRouteWrapper: React.FC = () => {
  const { slug, packageSlug } = useParams<{ slug: string; packageSlug: string }>();
  return <PackageHotpageDemo prospect={resolveProspect(slug)} packageSlug={packageSlug || 'pacote'} />;
};

const LandingPageRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <LandingPageDemo prospect={resolveProspect(slug)} />;
};

const BiolinkRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <BiolinkDemo prospect={resolveProspect(slug)} />;
};

const ResellerRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <ResellerDemo prospect={resolveProspect(slug)} />;
};

const LawRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <LawDemo prospect={resolveProspect(slug)} />;
};

const RealEstateRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <RealEstateDemo prospect={resolveProspect(slug)} />;
};

const RealEstateHotpageRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <RealEstateHotpage prospect={resolveProspect(slug)} />;
};

const DigitalMenuRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <DigitalMenuDemo prospect={resolveProspect(slug || 'restaurante')} />;
};

const HotpageRouteWrapper: React.FC = () => {
  const { slug, launchType } = useParams<{ slug?: string; launchType?: string }>();
  return <HotpageDemo prospect={resolveProspect(slug || 'prospect')} launchType={(launchType as any) || 'imovel'} />;
};

const AutoRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <AutoDemo prospect={resolveProspect(slug)} />;
};

const HealthRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <HealthDemo prospect={resolveProspect(slug)} />;
};

const ServiceRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <ServiceDemo prospect={resolveProspect(slug)} />;
};

const ArchRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <ArchDemo prospect={resolveProspect(slug)} />;
};

const AgencyRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <AgencyDemo prospect={resolveProspect(slug)} />;
};

const GastronomyRouteWrapper: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  return <GastronomyDemo prospect={resolveProspect(slug)} />;
};

export default App;
